import { useEffect, useCallback } from 'react';

interface ImageModalProps {
  imageUrl: string;
  filename: string;
  onClose: () => void;
}

export default function ImageModal({ imageUrl, filename, onClose }: ImageModalProps) {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle touch gestures for swipe-to-close
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startY = touch.clientY;
    const startTime = Date.now();

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const currentTouch = moveEvent.touches[0];
      const deltaY = currentTouch.clientY - startY;

      // Apply transform for visual feedback
      const modal = document.getElementById('image-modal-content');
      if (modal && deltaY > 0) {
        modal.style.transform = `translateY(${deltaY}px)`;
        modal.style.opacity = `${Math.max(0.3, 1 - deltaY / 500)}`;
      }
    };

    const handleTouchEnd = (endEvent: TouchEvent) => {
      const touch = endEvent.changedTouches[0];
      const deltaY = touch.clientY - startY;
      const deltaTime = Date.now() - startTime;
      const velocity = deltaY / deltaTime;

      // Close if swiped down significantly or with high velocity
      if (deltaY > 100 || velocity > 0.5) {
        onClose();
      } else {
        // Reset transform
        const modal = document.getElementById('image-modal-content');
        if (modal) {
          modal.style.transform = '';
          modal.style.opacity = '';
        }
      }

      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 animate-fadeIn"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="w-6 h-6 text-white"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Filename */}
      <div className="absolute top-4 left-4 right-16 text-white text-sm truncate bg-black/50 px-4 py-2 rounded-lg">
        {filename}
      </div>

      {/* Image container */}
      <div
        id="image-modal-content"
        className="relative max-w-[95vw] max-h-[95vh] transition-all"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
      >
        <img
          src={imageUrl}
          alt={filename}
          className="max-w-full max-h-[95vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
          draggable={false}
        />
      </div>

      {/* Swipe hint for mobile */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-sm flex items-center gap-2 md:hidden">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
        </svg>
        Swipe down to close
      </div>
    </div>
  );
}
